import React, { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createDildoGeometry } from '../utils/dildoGeometry';
import { createDildoMaterial } from '../utils/materialShader';
import { exportSTL, exportOBJ } from '../utils/exporters';

const DildoViewer = ({ params, isPanelOpen, onReady, onAutoRotateOff }) => {
  const mountRef = useRef(null);
  const rebuildRef = useRef(null);
  const autoRotateRef = useRef(true);
  const setSizeRef = useRef(null);
  const panelStateRef = useRef(isPanelOpen);

  useEffect(() => {
    panelStateRef.current = isPanelOpen;
    if (setSizeRef.current) setSizeRef.current();
  }, [isPanelOpen]);

  useEffect(() => {
    if (rebuildRef.current) rebuildRef.current(params);
  }, [params]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ── Renderer ──────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = 1.0;
    mount.appendChild(renderer.domElement);

    // ── Camera ────────────────────────────────────────────────────────────
    const camera = new THREE.PerspectiveCamera(42, 1, 0.01, 60);
    camera.position.set(0.8, 0.4, 5.0);

    const setSize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;

      const panelOpen = panelStateRef.current;
      const offsetX = (w > 768 && panelOpen) ? 184 : 0;
      const offsetY = (w <= 768 && panelOpen) ? h * 0.25 : 0;
      camera.setViewOffset(w, h, offsetX, offsetY, w, h);

      const targetAspect = 0.6;
      const mobileZoom = w <= 639 ? 0.75 : 1.0;
      camera.zoom = camera.aspect < targetAspect
        ? (camera.aspect / targetAspect) * mobileZoom
        : 1.0 * mobileZoom;
      camera.updateProjectionMatrix();
    };
    setSizeRef.current = setSize;

    // ── Scene ─────────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x030405);
    scene.fog = new THREE.FogExp2(0x030405, 0.022);
    setSize();

    // ── Controls ──────────────────────────────────────────────────────────
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 1.5;
    controls.maxDistance = 16;
    controls.target.set(0, 0, 0);

    controls.addEventListener('start', () => {
      if (autoRotateRef.current) {
        autoRotateRef.current = false;
        if (onAutoRotateOff) onAutoRotateOff();
      }
    });

    // ── Dildo group ───────────────────────────────────────────────────────
    const dildoGroup = new THREE.Group();
    scene.add(dildoGroup);

    let meshes = [];
    let mats = [];

    function rebuild(p) {
      // Dispose previous
      meshes.forEach(m => { dildoGroup.remove(m); m.geometry.dispose(); });
      mats.forEach(m => m.dispose());
      meshes = [];
      mats = [];

      // Main dildo mesh
      const geom = createDildoGeometry(p);
      const mat = createDildoMaterial(p);
      mats.push(mat);
      const mesh = new THREE.Mesh(geom, mat);
      mesh.castShadow = true;
      dildoGroup.add(mesh);
      meshes.push(mesh);

      // Update camera distance to fit length
      const L = p.length * 1.5;
      const dist = camera.position.distanceTo(controls.target);
      const targetDist = Math.max(controls.minDistance, Math.min(controls.maxDistance, 2.5 + L * 1.5));
      if (Math.abs(dist - targetDist) > 0.5) {
        const dir = camera.position.clone().sub(controls.target).normalize();
        camera.position.copy(controls.target).add(dir.multiplyScalar(targetDist));
        controls.saveState();
        controls.update();
        setSize();
      }
    }

    rebuild(params);
    rebuildRef.current = rebuild;

    // ── Animation ─────────────────────────────────────────────────────────
    let animId;
    const clock = new THREE.Clock();

    const animate = () => {
      animId = requestAnimationFrame(animate);

      if (autoRotateRef.current) {
        controls.autoRotate = false;
        const elapsed = clock.getElapsedTime();
        // Slow rotation around Y axis with gentle tilt oscillation
        const azimuth = elapsed * 0.35;
        const dist = camera.position.distanceTo(controls.target);
        const polar = Math.PI * 0.38 + Math.sin(elapsed * 0.18) * 0.12;

        const targetX = dist * Math.sin(polar) * Math.sin(azimuth);
        const targetY = dist * Math.cos(polar);
        const targetZ = dist * Math.sin(polar) * Math.cos(azimuth);

        camera.position.x += (targetX - camera.position.x) * 0.04;
        camera.position.y += (targetY - camera.position.y) * 0.04;
        camera.position.z += (targetZ - camera.position.z) * 0.04;
        camera.lookAt(controls.target);
      }

      controls.update();

      const cp = camera.position;
      mats.forEach(m => {
        if (m.uniforms?.uCameraPos) m.uniforms.uCameraPos.value.copy(cp);
      });

      renderer.render(scene, camera);
    };
    animate();

    // ── Resize ────────────────────────────────────────────────────────────
    const ro = new ResizeObserver(setSize);
    ro.observe(mount);

    // ── Expose exports ────────────────────────────────────────────────────
    if (onReady) {
      onReady({
        exportSTL: () => exportSTL(dildoGroup),
        exportOBJ: () => exportOBJ(dildoGroup),
        setAutoRotate: (v) => { autoRotateRef.current = v; },
      });
    }

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      mats.forEach(m => m.dispose());
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []); // intentional: runs once, params are accessed via rebuildRef

  return (
    <div ref={mountRef} data-testid="dildo-viewer" className="absolute inset-0" style={{ cursor: 'grab' }}>
      {/* Scan-line overlay */}
      <div className="absolute inset-0 pointer-events-none z-10"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px)' }} />
      {/* Corner brackets */}
      <div className="absolute top-20 left-4 w-8 h-8 border-t border-l border-[#00F0FF]/30 pointer-events-none z-20" />
      <div className={`absolute top-20 w-8 h-8 border-t border-r border-[#00F0FF]/30 pointer-events-none z-20 transition-all duration-300 ${isPanelOpen ? 'right-4 md:right-[368px]' : 'right-4'}`} />
      <div className={`absolute left-4 w-8 h-8 border-b border-l border-[#00F0FF]/30 pointer-events-none z-20 transition-all duration-300 ${isPanelOpen ? 'bottom-[calc(50vh+16px)] md:bottom-4' : 'bottom-4'}`} />
      <div className={`absolute w-8 h-8 border-b border-r border-[#00F0FF]/30 pointer-events-none z-20 transition-all duration-300 ${isPanelOpen ? 'right-4 md:right-[368px] bottom-[calc(50vh+16px)] md:bottom-4' : 'right-4 bottom-4'}`} />
      {/* HUD */}
      <div className={`absolute left-6 pointer-events-none z-20 flex flex-col gap-1 transition-all duration-300 ${isPanelOpen ? 'bottom-[calc(50vh+24px)] md:bottom-6' : 'bottom-6'}`}>
        <span className="text-[10px] font-mono tracking-[0.2em] text-[#00F0FF]/25 uppercase">Drag to rotate · Scroll to zoom</span>
      </div>
    </div>
  );
};

export default DildoViewer;
